import { useRequestCode } from '@fidalia/api';
import { Alert, Button, Divider, Field, GoogleIcon, IconBadge, Input } from '@fidalia/ui';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { MdMail, MdMarkEmailUnread } from 'react-icons/md';
import { useState } from 'react';
import { z } from 'zod';

import { env } from '@/env';
import { AuthLayout } from '@/features/auth/auth-layout';
import { OtpStep } from '@/features/auth/otp-step';
import { errorMessage, OTP_LENGTH } from '@/lib/auth';
import { clearFieldErrors, collectIssues, fieldError, useAttemptErrors } from '@/lib/form';
import { SUPPORT_EMAIL } from '@/lib/links';

const emailFormat = z.string().trim().email('Adresse email invalide.');

/** Schéma de saisie : ne juge que le format, un champ vide reste acceptable. */
const draftSchema = z.object({
  email: z.string().refine((value) => value === '' || emailFormat.safeParse(value).success, {
    message: 'Adresse email invalide.',
  }),
});

/** Schéma complet, appliqué au clic sur « Continuer ». */
const loginSchema = z.object({
  email: z.string().trim().min(1, 'Renseignez votre email.').pipe(emailFormat),
});

/**
 * Motifs de renvoi vers cet écran, posés par le garde de `_app` ou par le
 * client API sur un 401. Inconnu ou absent : aucun bandeau, la page s'affiche
 * telle quelle — un code inventé dans l'URL ne doit rien pouvoir annoncer.
 */
const RAISONS: Record<string, string> = {
  cabinet_desactive:
    'L’accès de votre cabinet a été désactivé par l’administrateur. Contactez le support pour le rétablir.',
};

export const Route = createFileRoute('/login')({
  // Type de retour optionnel, sinon TanStack exige un `search` explicite sur
  // chaque `<Link to="/login">` de l'application.
  validateSearch: (search: Record<string, unknown>): { raison?: string } =>
    typeof search.raison === 'string' ? { raison: search.raison } : {},
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { raison } = Route.useSearch();
  const messageRaison = raison ? RAISONS[raison] : undefined;
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const attempt = useAttemptErrors();
  const requestCode = useRequestCode();

  const form = useForm({
    defaultValues: { email: '' },
    // Validation au blur uniquement : on ne corrige pas l'utilisateur pendant
    // qu'il tape. Les erreurs sont effacées à la frappe (`clearFieldErrors`).
    validators: { onBlur: draftSchema },
  });

  const submit = async () => {
    setFormError(null);
    const result = loginSchema.safeParse(form.state.values);
    if (!result.success) {
      attempt.setErrors(collectIssues(result.error));
      return;
    }
    attempt.reset();

    const email = form.state.values.email.trim();
    try {
      await requestCode.mutateAsync({ email });
      setSentTo(email);
    } catch (error) {
      setFormError(errorMessage(error, 'Envoi impossible.'));
    }
  };

  if (sentTo) {
    return (
      <AuthLayout
        title="Vérifier votre email"
        subtitle={
          <>
            Veuillez saisir le code à {OTP_LENGTH} chiffres envoyé à{' '}
            <span className="font-medium text-content">{sentTo}</span>.
          </>
        }
        icon={
          <IconBadge size="2xl">
            <MdMarkEmailUnread />
          </IconBadge>
        }
      >
        <OtpStep
          email={sentTo}
          onBack={() => setSentTo(null)}
          onVerified={() => navigate({ to: '/' })}
          submitLabel="Se connecter"
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Se connecter"
      subtitle="Connectez-vous à votre espace Fidalia."
      footer={
        <>
          Besoin d’aide ?{' '}
          <a
            className="font-medium text-brand-600 hover:underline"
            href={`mailto:${SUPPORT_EMAIL}`}
          >
            {SUPPORT_EMAIL}
          </a>
        </>
      }
    >
      {messageRaison ? (
        <Alert tone="danger" title="Accès désactivé">
          {messageRaison}
        </Alert>
      ) : null}
      {formError ? <Alert tone="danger">{formError}</Alert> : null}

      <Button
        type="button"
        variant="secondary"
        size="lg"
        fullWidth
        icon={<GoogleIcon className="size-6" />}
        // Navigation pleine page vers l'origine **absolue** de l'API, pas via
        // `/api` : le cookie de `state` doit être posé et relu sur le même
        // hôte que le retour de Google, sinon il n'est jamais renvoyé.
        onClick={() => globalThis.location.assign(`${env.VITE_API_ORIGIN}/auth/google`)}
      >
        Se connecter avec Google
      </Button>

      <Divider>Ou bien</Divider>

      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <form.Field name="email">
          {(field) => {
            const error = fieldError(field, attempt.errors);
            return (
              <Field label="Email" htmlFor={field.name} error={error}>
                <Input
                  id={field.name}
                  name={field.name}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="cabinet@exemple.fr"
                  icon={<MdMail className="size-6" />}
                  invalid={Boolean(error || formError)}
                  value={field.state.value}
                  onChange={(event) => {
                    attempt.clear(field.name);
                    clearFieldErrors(field);
                    field.handleChange(event.target.value);
                  }}
                  onBlur={field.handleBlur}
                />
              </Field>
            );
          }}
        </form.Field>

        <Button type="submit" size="lg" fullWidth loading={requestCode.isPending} className="mt-2">
          Continuer
        </Button>
      </form>

      <p className="text-center text-base text-brand-600/70">
        Pas encore inscrit ?{' '}
        <Link to="/register" className="font-semibold text-brand-600 hover:underline">
          Créer un compte
        </Link>
      </p>
    </AuthLayout>
  );
}
